import { IObjectConfig } from "../../../vendor_core/core/src/baseConfigs/layoutConfigShema";
import { ConfigurationTypes } from "../../../vendor_core/core/src/const/ConfigurationTypes";
import ConfigManager from "../../../vendor_core/core/src/managers/ConfigManager";
import ISymbolSpineAnimConfig from "../../../vendor_core/core/src/managers/contracts/configurations/ISymbolSpineAnimConfig";
import { Tween } from "../../../vendor_core/core/src/Tween";
import GameLoader from "../../../vendor_core/core/src/utils/GameLoader";
import Layout from "../../../vendor_core/core/src/utils/Layout";
import ReelSymbol from "../../../vendor_core/core/src/views/Symbol";
import SymbolSpineAnimation from "../../../vendor_core/core/src/views/SymbolSpineAnimation";
import { TrackEntry } from "@pixi-spine/runtime-3.8";
import { Jackpot } from "../types/IBURJackpotsModel";
/**
 * Abstraction for a single symbol.
 * A reel contains several of those.
 */
export default class BURReelSymbol extends ReelSymbol {
    public spineAnim: SymbolSpineAnimation = null;
    private blurLevel: number = 0;
    private isMystery: boolean = false;
    private isLocked: boolean = false;

    constructor() {
        super();
    }

    create(
        symBounds: IObjectConfig,
        positionDefinitions: Map<string, IObjectConfig>
    ) {
        this.bounds = symBounds;
        this.positionDefinitions = positionDefinitions;
        this.createSpineAnim();
    }
    /**
     * Play symbol spine animation
     * @public
     * @param animState: default get value from config (if set playDefault: true and defaultState: <some state>, in config),
     *  or state or chaining state for current animation witch is defined in-game config
     * @param loop: default is get value from config
     * @param timeScale: default is get value from config (if set in config) or set to original speed (1)
     * @returns void
     */
    public playSpineAnim = (
        animName?: string,
        loop?: boolean,
        timeScale?: number
    ) => {
        this.spineAnim.setActiveSkin(this.id);
        if (animName) {
            if (this.spineAnim.state.hasAnimation(animName)) {
                this.spineAnim.play(animName, loop, timeScale);
                this.showActiveAnim();
            } else if (this.spineAnim.hasChainingAnim(animName)) {
                this.spineAnim.playChainingStates(animName, timeScale, loop);
                this.showActiveAnim();
            } else {
                this.show();
                console.warn(
                    `[SPINE ANIMATION] -> Spine <${this.spineAnim.animConfig.name}> animation <${animName}> is missing from states: <${this.spineAnim.animConfig.states}> or chaining states <${this.spineAnim.animConfig.getSkinConfig(this.spineAnim.activeSkin).chainingStates}> animation! `
                );
            }
        } else {
            this.defaultPlaySpineAnim();
        }
    };

    public playAsync = (animName: string): Promise<boolean> => {
        return new Promise<boolean>((res) => {
            this.spineAnim.setActiveSkin(this.id);
            if (this.spineAnim.state.hasAnimation(animName)) {
                this.spineAnim.play(animName);
                this.showActiveAnim();
                Tween.delayedCall(
                    this.spineAnim.getAnimDuration(animName),
                    () => {
                        res();
                    },
                    [],
                    this
                );
            } else {
                this.show();
                console.warn(
                    `[SPINE ANIMATION] -> Spine <${this.spineAnim.animConfig.name}> animation <${animName}> is missing from states: <${this.spineAnim.animConfig.states}>`
                );
                res(true);
            }
        });
    };

    public playAsyncSpineAnim = (
        animName: string,
        timeScale?: number
    ): Promise<boolean> => {
        return new Promise<boolean>((res) => {
            this.spineAnim.setActiveSkin(this.id);
            if (animName) {
                if (this.spineAnim.state.hasAnimation(animName)) {
                    this.spineAnim.playAsync(animName, timeScale).then(() => {
                        res();
                    });
                    this.showActiveAnim();
                } else if (this.spineAnim.hasChainingAnim(animName)) {
                    this.spineAnim
                        .playAsyncChainingStates(animName, timeScale)
                        .then(() => {
                            res();
                        });
                    this.showActiveAnim();
                } else {
                    this.show();
                    console.warn(
                        `[SPINE ANIMATION] -> Spine <${this.spineAnim.animConfig.name}> animation <${animName}> is missing from states: <${this.spineAnim.animConfig.states}> or chaining states <${this.spineAnim.animConfig.getSkinConfig(this.spineAnim.activeSkin).chainingStates}> animation! `
                    );
                    res();
                }
            }
        });
    };

    protected defaultPlaySpineAnim = () => {
        this.spineAnim.setActiveSkin(this.id);
        let hasPlayDefault = this.spineAnim.animConfig.getSkinConfig(
            this.spineAnim.activeSkin
        ).playDefault;
        if (hasPlayDefault) {
            this.spineAnim.play();
            this.showActiveAnim();
        }
    };
    protected createSpineAnim = (): void => {
        let config = ConfigManager.getConfig(
            ConfigurationTypes.SYMBOL_SPINE_ANIMATIONS
        ) as ISymbolSpineAnimConfig;

        let anim = new SymbolSpineAnimation(
            GameLoader.loader.resources[`spine~${config.name}~json`].spineData,
            config.name,
            config
        );
        anim.scale.set(Layout.globalScale);
        this.spineAnim = anim;
        this.addChild(anim as any);
    };
    public hideActiveAnim = () => {
        this.spineAnim.hide();
    };
    public showActiveAnim = () => {
        this.spineAnim.show();
    };
    public removeSpineAnim = (showSym = true) => {
        if (this.spineAnim) {
            if (showSym) {
                this.show();
            }
            this.removeChild(this.spineAnim as any);
            this.spineAnim.stop();
            this.spineAnim.destroy();
            this.spineAnim = null;
        }
    };
    public setId(id: number) {
        const idleAnim = this.isMystery ? "idle_mystery" : "idle";
        if (this.id === id) {
            // If it is the same symbol id, check if it has changed to mystery
            // Redraw only if it has changed
            const entry = this.spineAnim.state.tracks[0] as TrackEntry;
            if (!entry || entry.animation.name !== idleAnim) {
                this.id = id;
                this.spineAnim.setActiveSkin(id);
                this.spineAnim.play(idleAnim);
            }
        } else {
            this.id = id;
            this.spineAnim.setActiveSkin(id);
            this.spineAnim.play(idleAnim);
        }
    }

    public setIsMystery(isMystery: boolean) {
        this.isMystery = isMystery;
    }

    public setIsLocked(isLocked: boolean) {
        this.isLocked = isLocked;
    }

    public get locked(): boolean {
        return this.isLocked;
    }

    public setJackpotFragment(tier: Jackpot) {
        let id: number;
        switch (tier) {
            case "MINI":
                id = 30;
                break;
            case "MINOR":
                id = 31;
                break;
            case "MEDIUM":
                id = 32;
                break;
            case "MEGA":
                id = 33;
                break;
            case "MAJOR":
                id = 34;
                break;
            default:
                id = this.id;
                break;
        }
        this.id = id;
        this.spineAnim.setActiveSkin(id);
        this.spineAnim.play("idle_mystery");
    }

    //----------not used for spine symbols-------
    setBlurLevel(level: number) {
        this.blurLevel = level;
    }
    public showWin(loop: boolean = false) {}
    fade() {}
    hideWin() {}
    public hideDebugTag() {}
    //-------------------------------------------
}
